import React, { useState } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import type { SreInvestigateTarget } from '../types';
import {
  getSreInvestigationMode,
  isInvestigatableState,
  isSreInvestigationAvailable,
} from '../constants';
import { ensureSreFontsLoaded } from '../styles/theme';
import SreInvestigatePanel from './SreInvestigatePanel';

export interface SreInvestigateButtonProps extends SreInvestigateTarget {
  className?: string;
  onPosted?: () => void;
  forceShow?: boolean;
  /** When true (default), investigation starts as soon as the panel opens. */
  autoStart?: boolean;
}

/**
 * Pulse-native CTA to open the Investigation workbench.
 */
const SreInvestigateButton: React.FC<SreInvestigateButtonProps> = ({
  className = '',
  onPosted,
  forceShow = false,
  autoStart = true,
  ...target
}) => {
  const [open, setOpen] = useState(false);
  const { ticketNumber, ticketState } = target;

  React.useEffect(() => {
    ensureSreFontsLoaded();
  }, []);

  if (!forceShow && !isSreInvestigationAvailable()) {
    return null;
  }

  if (!ticketNumber?.trim()) {
    return null;
  }

  const mode = getSreInvestigationMode();
  const enabled = isInvestigatableState(ticketState);
  const disabledTitle = !enabled
    ? `Investigation is only available for active ticket states (current: ${ticketState || 'unknown'}).`
    : undefined;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={!enabled}
        title={disabledTitle}
        aria-label={`Open SRE Investigation workbench for ${ticketNumber}`}
        className={[
          'group inline-flex items-center gap-2 rounded-lg',
          'border border-primary-200 bg-white',
          'px-3.5 py-2 text-sm font-semibold text-primary-700',
          'shadow-sm shadow-primary-600/5',
          'transition duration-150',
          'hover:border-primary-300 hover:bg-primary-50 hover:text-primary-900',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
          'disabled:cursor-not-allowed disabled:opacity-50',
          className,
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {/* <span className="flex h-5 w-5 items-center justify-center rounded bg-primary-600 text-white transition group-hover:bg-primary-700">
          <MagnifyingGlassIcon className="h-3 w-3" aria-hidden />
        </span> */}
        <span>SRE Investigation</span>
        {/* {mode === 'demo' && (
          <span className="rounded bg-secondary-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-secondary-800">
            Demo
          </span>
        )} */}
      </button>

      <SreInvestigatePanel
        open={open}
        onClose={() => setOpen(false)}
        target={target}
        onPosted={onPosted}
        autoStart={autoStart}
      />
    </>
  );
};

export default SreInvestigateButton;
